import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/core';

import { t } from '../i18n';
import { EditorFormatControls } from './EditorFormatControls';
import { Glyph, ICON } from './icons';
import { TurnIntoMenu, type TurnIntoItem } from './SelectionActionMenus';

const VIEWPORT_GAP = 8;

/** Dock touch editing to the visible bottom edge, including above the keyboard. */
export function InsertBar({
  editor,
  onInsert,
  onComment,
  moving,
  onToggleMove,
  turnItems,
  onTurnInto,
  commentComposer,
}: {
  editor: Editor;
  onInsert: () => void;
  onComment: () => void;
  moving: boolean;
  onToggleMove: () => void;
  turnItems: TurnIntoItem[];
  onTurnInto: (id: string) => void;
  commentComposer?: React.ReactNode;
}): React.ReactElement {
  const barRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: window.innerHeight });
  const [, setTransaction] = useState(0);
  const [turnOpen, setTurnOpen] = useState(false);
  const [clipboardMessage, setClipboardMessage] = useState('');
  const composing = Boolean(commentComposer);
  const selected = !editor.state.selection.empty;

  async function clipboardAction(action: 'copy' | 'cut' | 'paste'): Promise<void> {
    const { from, to } = editor.state.selection;
    const before = editor.state.doc;
    setClipboardMessage('');
    try {
      if (action === 'paste') {
        const text = await navigator.clipboard.readText();
        if (!text || editor.isDestroyed) return;
        if (!editor.state.doc.eq(before)) {
          setClipboardMessage(t('editor.clipboardChanged'));
          return;
        }
        editor.commands.setTextSelection({ from, to });
        editor.view.pasteText(text);
        editor.commands.focus();
      } else {
        await navigator.clipboard.writeText(before.textBetween(from, to, '\n'));
        if (action === 'cut' && !editor.isDestroyed) {
          if (!editor.state.doc.eq(before)) {
            setClipboardMessage(t('editor.clipboardChanged'));
            return;
          }
          editor.chain().focus().setTextSelection({ from, to }).deleteSelection().run();
        }
      }
    } catch {
      setClipboardMessage(t('editor.clipboardDenied'));
    }
  }

  useLayoutEffect(() => {
    const update = (): void => {
      const bar = barRef.current;
      if (!bar || editor.isDestroyed) return;
      const viewport = window.visualViewport;
      const canvasTop = editor.view.dom.closest('main')?.getBoundingClientRect().top ?? 0;
      const viewportTop = Math.max(viewport?.offsetTop ?? 0, canvasTop) + VIEWPORT_GAP;
      const viewportLeft = (viewport?.offsetLeft ?? 0) + VIEWPORT_GAP;
      const viewportRight =
        viewportLeft + (viewport?.width ?? window.innerWidth) - 2 * VIEWPORT_GAP;
      const viewportBottom =
        (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight) - VIEWPORT_GAP;
      const { height, width } = bar.getBoundingClientRect();
      const maxTop = Math.max(viewportTop, viewportBottom - height);
      const left = viewportLeft + (viewportRight - viewportLeft - width) / 2;
      setPosition({
        top: maxTop,
        maxHeight: Math.max(0, viewportBottom - viewportTop),
        left: Math.max(viewportLeft, Math.min(left, viewportRight - width)),
      });
    };
    const transaction = (): void => {
      update();
      setTransaction((value) => value + 1);
    };
    update();
    editor.on('transaction', transaction);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    const observer = new ResizeObserver(update);
    if (barRef.current) observer.observe(barRef.current);
    return () => {
      editor.off('transaction', transaction);
      observer.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [editor, composing]);

  return createPortal(
    <div
      ref={barRef}
      className={composing ? 'insertbar__compose' : 'insertbar'}
      role={composing ? undefined : 'toolbar'}
      aria-label={composing ? undefined : t('editor.formatting')}
      style={position}
    >
      {commentComposer ?? (
        <>
          <div className="insertbar__row">
            <button
              type="button"
              className="insertbar__btn nb"
              aria-label={t('editor.insertBlock')}
              disabled={moving}
              onPointerDown={(event) => event.preventDefault()}
              onClick={onInsert}
            >
              <Glyph d={ICON.plus} size={20} />
            </button>
            <TurnIntoMenu
              items={turnItems}
              open={turnOpen}
              onOpenChange={setTurnOpen}
              onTurnInto={(id) => {
                onTurnInto(id);
                editor.commands.focus();
              }}
              label={t('editor.turnInto')}
              side="top"
              portalContainer={barRef.current}
              trigger={
                <button
                  type="button"
                  className="insertbar__turn nb"
                  disabled={moving}
                  aria-label={t('editor.turnInto')}
                >
                  {t('editor.turnInto')}
                  <Glyph d="m6 15 6-6 6 6" size={12} />
                </button>
              }
            />
            <button
              type="button"
              className="insertbar__btn nb"
              aria-label={t('editor.comment')}
              disabled={!selected || moving}
              onPointerDown={(event) => event.preventDefault()}
              onClick={onComment}
            >
              <Glyph d={ICON.comment} size={20} />
            </button>
            <button
              type="button"
              className="insertbar__btn nb"
              aria-label={t(moving ? 'editor.finishMoving' : 'editor.moveBlocks')}
              aria-pressed={moving}
              onPointerDown={(event) => event.preventDefault()}
              onClick={onToggleMove}
            >
              <Glyph
                d={
                  moving
                    ? 'm5 12 4 4L19 6'
                    : [
                        'M12 3v18',
                        'M3 12h18',
                        'm8 7 4-4 4 4',
                        'm8 17 4 4 4-4',
                        'm7 8-4 4 4 4',
                        'm17 8 4 4-4 4',
                      ]
                }
                size={20}
              />
            </button>
          </div>
          {moving ? (
            <p className="insertbar__hint" role="status">
              {t('editor.moveBlocksHint')}
            </p>
          ) : (
            <div className="insertbar__formats">
              <EditorFormatControls editor={editor} keepEditorFocus />
              <span className="selbar__sep" aria-hidden="true" />
              <button
                type="button"
                className="insertbar__btn nb"
                aria-label={t('editor.copyText')}
                disabled={!selected}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => void clipboardAction('copy')}
              >
                <Glyph d={ICON.copy} size={18} />
              </button>
              <button
                type="button"
                className="insertbar__btn nb"
                aria-label={t('editor.cutText')}
                disabled={!selected}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => void clipboardAction('cut')}
              >
                <Glyph
                  d={[
                    'M9 9 21 3',
                    'm9 15 12 6',
                    'M9 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0',
                    'M9 17a3 3 0 1 1-6 0 3 3 0 0 1 6 0',
                  ]}
                  size={18}
                />
              </button>
              <button
                type="button"
                className="insertbar__btn nb"
                aria-label={t('editor.pasteText')}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => void clipboardAction('paste')}
              >
                <Glyph d={['M9 4H5v17h14V4h-4', 'M9 2h6v5H9z']} size={18} />
              </button>
            </div>
          )}
          {clipboardMessage ? (
            <p className="insertbar__hint" role="status">
              {clipboardMessage}
            </p>
          ) : null}
        </>
      )}
    </div>,
    document.body,
  );
}
